import React, {
	createContext, ReactNode, useCallback, useContext, useMemo, useState
} from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../reducers'
import { Organization } from '../reducers/user/userActions'

export const SUPER_ORG_MODE_STORAGE_KEY = 'umyjeme:superOrganizationMode'

/**
 * Obrazovky, kde super-org funkcia existuje. Pri pridani dalsej obrazovky dopis cestu sem
 * A do tabulky v .planning/concepts/super-organization.md §4.2
 *
 * Porovnava sa PRESNA cesta, nie route key - `paths:jobs|key`, `jobDetailID|key`
 * aj `jobCreate|key` maju zhodnu hodnotu "jobs".
 */
export const SUPER_ORG_ENABLED_PATHS: string[] = ['/prace']

/**
 * Rezim je predvolene ZAPNUTY pre kazdeho, kto ho ma dostupny. Vypnuty je len vtedy,
 * ked si ho pouzivatel sam vypol - preto sa 'false' uklada explicitne a chybajuci kluc
 * (prvy prihlas, vycisteny storage, prepnutie organizacie) znamena zapnuty rezim.
 */
const readStoredMode = (): boolean => {
	try {
		return window.localStorage.getItem(SUPER_ORG_MODE_STORAGE_KEY) !== 'false'
	} catch (error) {
		return true
	}
}

const writeStoredMode = (value: boolean) => {
	try {
		window.localStorage.setItem(SUPER_ORG_MODE_STORAGE_KEY, value ? 'true' : 'false')
	} catch (error) {
		// localStorage nemusi byt dostupny (privatny rezim) - rezim potom len neprezije reload
	}
}

interface SuperOrganizationModeContextValue {
	storedActive: boolean
	setStoredActive: (value: boolean) => void
}

const SuperOrganizationModeContext = createContext<SuperOrganizationModeContextValue>({
	storedActive: true,
	setStoredActive: () => {}
})

export const SuperOrganizationModeProvider = ({ children }: { children: ReactNode }) => {
	const [storedActive, setStoredActiveState] = useState<boolean>(readStoredMode)

	const setStoredActive = useCallback((value: boolean) => {
		writeStoredMode(value)
		setStoredActiveState(value)
	}, [])

	const value = useMemo(() => ({ storedActive, setStoredActive }), [storedActive, setStoredActive])

	return (
		<SuperOrganizationModeContext.Provider value={value}>
			{children}
		</SuperOrganizationModeContext.Provider>
	)
}

export interface UseSuperOrganizationMode {
	isAvailable: boolean
	isActive: boolean
	organizations: Organization[]
	toggle: () => void
	reset: () => void
}

export const useSuperOrganizationMode = (): UseSuperOrganizationMode => {
	const { storedActive, setStoredActive } = useContext(SuperOrganizationModeContext)
	const superOrganization = useSelector((state: RootState) => state.profile.profile.data?.superOrganization)

	const isAvailable = superOrganization?.available || false

	// Ked pouzivatel stratí opravnenie, rezim sa vypne sam - bez toho, aby sa niekde volalo reset()
	const isActive = isAvailable && storedActive

	const toggle = useCallback(() => setStoredActive(!storedActive), [storedActive, setStoredActive])
	const reset = useCallback(() => setStoredActive(false), [setStoredActive])

	return {
		isAvailable,
		isActive,
		organizations: superOrganization?.organizations || [],
		toggle,
		reset
	}
}

export const useIsSuperOrganizationScreen = (): boolean => {
	const location = useLocation()
	return SUPER_ORG_ENABLED_PATHS.includes(location.pathname)
}
